import React, { FC, ReactNode, useState } from 'react';
import { BodyText, FlexCol, FlexRow } from '@minecraft.atoms';
import { CardNumberElement } from '@recurly/react-recurly';
import styled from 'styled-components';
import { useFormikContext } from 'formik';

const StyledRecurly = styled(CardNumberElement)<{ $error?: boolean; $focused?: boolean }>`
  // We have to style the sub-div due to Recurlys iFrame wrapping around the element
  & > div {
    height: 2.5rem;
    padding: 0 0.5rem;
    border: 1px solid
      ${({ theme, $error }) =>
        $error ? theme.designSystem.borderColors.error : theme.designSystem.borderColors.default};
    border-radius: 0.25rem;
    background-color: ${({ theme }) => theme.designSystem.bgColors.card};

    outline: ${({ theme, $focused }) =>
      $focused ? `4px solid ${theme.designSystem.borderColors.inputFocus}` : undefined};
  }
`;

type StyledRecurlyInput = {
  label: string;
  name: string;
  placeholder?: string;
  hideLabel?: boolean;
  required?: boolean;
  RecurlyInput: ReactNode;
  EndSlotComponent?: ReactNode;
  LabelEndSlotComponent?: ReactNode;
  inputProps?: { className?: string; dataQaId?: string };
};

export const StyledRecurlyInput: FC<StyledRecurlyInput> = ({
  label,
  name,
  placeholder = '',
  hideLabel = false,
  required,
  // The Recurly Element to use (CC, CVV, month, year are all sensitive fields that need an iFrame wrapped)
  RecurlyInput,
  EndSlotComponent,
  LabelEndSlotComponent,
  inputProps,
}) => {
  const [focused, setFocused] = useState(false);
  const { errors } = useFormikContext();

  const dataQaId = inputProps?.dataQaId;

  return (
    <FlexCol data-qa-id={dataQaId} gap="1" className="cn_w-full">
      {!hideLabel && (
        <FlexRow justifyContent="space-between" alignItems="center" w="full">
          <BodyText as="label" fontWeight="semibold" htmlFor={name}>
            {label}
            {required && ' *'}
          </BodyText>
          {LabelEndSlotComponent && <div>{LabelEndSlotComponent}</div>}
        </FlexRow>
      )}
      <FlexRow alignItems="center" wrap="nowrap" className="cn_w-full">
        <StyledRecurly
          className={inputProps?.className}
          as={RecurlyInput}
          $focused={focused}
          $error={!!errors[name]}
          id={name}
          {...(hideLabel ? { 'aria-label': label } : {})}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            placeholder: {
              content: placeholder,
              fontWeight: '100',
            },
          }}
          aria-placeholder={placeholder}
        />
        {EndSlotComponent && <div className="cn_atom_pl-2">{EndSlotComponent}</div>}
      </FlexRow>
      {errors[name] ? <BodyText color="error">{errors[name]}</BodyText> : null}
    </FlexCol>
  );
};
